import React from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import styled, { css } from 'styled-components'
import { oktaLoginRedirect } from '@/utils/okta/oktaAuth'
import { PATHS } from '@/utils/routes/paths'

// Okta 로그인 페이지(랜딩) — Figma 2:88443.
// [Okta 로그인] → Okta 호스팅 로그인으로 redirect, [회원가입] → Okta 회원가입.
// LOGISPRD-11076 이후 이 화면이 유일한 로그인 표면이다. 진입 경로: /auth/login · /auth/okta-login.
function OktaLogin() {
  const navigate = useNavigate()

  // auth 레이아웃(AuthContentWrap)의 transform 으로 position:fixed 가 viewport 기준이 안 되므로
  // createPortal 로 document.body 에 직접 렌더해 흰 배경이 전체를 덮게 한다(SignUp 과 동일 패턴).
  return createPortal(
    <OktaLanding>
      <LandingInner>
        <LandingTitleBlock>
          <LandingLogo>MOMS</LandingLogo>
          <LandingTitle>로그인</LandingTitle>
          <LandingSubtitle>MUSINSA Order Management System</LandingSubtitle>
        </LandingTitleBlock>
        <LandingDivider />
        <LandingButtons>
          <PrimaryBtn type="button" onClick={() => oktaLoginRedirect()}>
            Okta 로그인
          </PrimaryBtn>
          <OutlinedBtn type="button" onClick={() => navigate(PATHS['auth.okta-signup'])}>
            회원가입
          </OutlinedBtn>
        </LandingButtons>
      </LandingInner>
    </OktaLanding>,
    document.body,
  )
}

export default OktaLogin

/* ───────────── 리뉴얼 로그인 랜딩 (Figma 2:88443) ───────────── */

// 루트: bg --bg(#fff) — auth 레이아웃 배경이미지를 흰색으로 덮고 중앙 정렬
const OktaLanding = styled.div`
  position: fixed;
  inset: 0;
  z-index: 100;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
`

// 15:32525 — gap spacing/600=24, width 335, 중앙
const LandingInner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: center;
  width: 335px;
  max-width: calc(100% - 32px);
`

// 15:32485 title — gap spacing/200=8, width 271
const LandingTitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
  width: 271px;
`

// 15:32486 moms-logotype (75x24)
const LandingLogo = styled.div`
  font-size: 22px;
  font-weight: 800;
  letter-spacing: 0.04em;
  line-height: 24px;
  color: #000;
`

// 2:88449 — title-xl-medium: Pretendard Medium 30 / lh1.4 / fg #000
const LandingTitle = styled.p`
  margin: 0;
  font-size: 30px;
  font-weight: 500;
  line-height: 1.4;
  color: #000;
  text-align: center;
`

// 2:88450 — text-xl: Pretendard Regular 16 / lh1.36 / fg-lower #8a8a8a
const LandingSubtitle = styled.p`
  margin: 0;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.36;
  color: #8a8a8a;
  text-align: center;
`

// 15:32513 Divider Horizontal — 1px
const LandingDivider = styled.div`
  width: 100%;
  height: 1px;
  background: #ebebeb;
`

// 14:16362 Button — gap 12, width full
const LandingButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`

// 공통 버튼 치수: h52(min/max) / px spacing/500=20 py spacing/400=16 / rounded/xs=4 / label 15 Medium lh1.36
const landingButtonBase = css`
  width: 100%;
  min-height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 16px 20px;
  border-radius: 4px;
  font-size: 15px;
  font-weight: 500;
  line-height: 1.36;
  cursor: pointer;
`

// 14:16336 primaryButton — bg --button/primary/bg #000 / fg-contrast #fff
const PrimaryBtn = styled.button`
  ${landingButtonBase}
  border: none;
  background: #000;
  color: #fff;

  &:hover {
    opacity: 0.85;
  }
`

// 14:16341 primaryButton(outlined) — bg #fff / border 1px(--functional/borderWidth/light) #000 / fg #000
const OutlinedBtn = styled.button`
  ${landingButtonBase}
  border: 1px solid #000;
  background: #fff;
  color: #000;

  &:hover {
    background: #f5f5f5;
  }
`
